import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle, Card } from '../styles/StyledComponents';

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const SkillCard = styled(Card)`
  padding: 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`;

const SkillIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--light-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 40px;
    height: 40px;
    color: var(--primary-blue);
  }
`;

const SkillTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--dark-blue);
`;

const SkillDescription = styled.p`
  color: var(--medium-gray);
  margin-bottom: 1.5rem;
`;

const SkillsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
  width: 100%;
`;

const SkillItem = styled.li`
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--primary-blue);
  }
`;

const Skills: React.FC = () => {
  const skillsData = [
    {
      title: "Medical Coding",
      description: "Expertise in healthcare coding systems and standards",
      skills: ["ICD-10", "CPT", "HCPCS", "Coding Audits", "Reimbursement Optimization"]
    },
    {
      title: "Administrative Expertise",
      description: "Streamlining workflows and ensuring operational efficiency",
      skills: ["Documentation Management", "Workflow Optimization", "Team Leadership", "Regulatory Compliance"]
    },
    {
      title: "Healthcare Technology",
      description: "Proficiency with modern healthcare systems and platforms",
      skills: ["EHR/EMR Systems", "Practice Management Tools", "Healthcare Analytics", "Telehealth Solutions"]
    },
    {
      title: "Revenue Cycle Management",
      description: "Supporting financial health of medical practices",
      skills: ["Claims Processing", "Insurance Verification", "Prior Authorizations", "Billing Optimization"]
    },
    {
      title: "Pharmacy Management",
      description: "Comprehensive pharmaceutical care and operations",
      skills: ["Medication Dispensing", "Patient Counseling", "Inventory Control", "Regulatory Compliance"]
    },
    {
      title: "Emerging Technologies",
      description: "Staying at the forefront of healthcare innovation",
      skills: ["AI in Healthcare", "Automation Tools", "Digital Health Solutions", "Data-Driven Decision Making"]
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    })
  };

  return (
    <Section id="skills">
      <Container>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Skills & Expertise
        </SectionTitle>
        
        <SkillsGrid>
          {skillsData.map((skill, index) => (
            <motion.div
              key={index}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
            >
              <SkillCard>
                <SkillIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </SkillIcon>
                <SkillTitle>{skill.title}</SkillTitle>
                <SkillDescription>{skill.description}</SkillDescription>
                <SkillsList>
                  {skill.skills.map((item, i) => (
                    <SkillItem key={i}>{item}</SkillItem>
                  ))}
                </SkillsList>
              </SkillCard>
            </motion.div>
          ))}
        </SkillsGrid>
      </Container>
    </Section>
  );
};

export default Skills;
